import { FC } from 'react';
import NextLink from 'next/link';
import { Box, Flex, Heading, Link, Text } from '@chakra-ui/react';
import { useLocalization } from '~/framework';
import { useSiteInfo } from '~/pages/_shared';

const errorCodes = [400, 403, 404, 500, 503] as const;
type ErrorCode = (typeof errorCodes)[number];

function isErrorCode(statusCode: number): statusCode is ErrorCode {
   return (errorCodes as any).includes(statusCode);
}

function hasContent(text: string | undefined): boolean {
   return text !== undefined && text.length > 0;
}

export type ErrorMessage = {
   code: number;
   title?: string;
   description?: string;
};

export const ErrorPage: FC<{ error: ErrorMessage }> = ({ error }) => {
   const { _js } = useLocalization();
   const { data: siteInfo } = useSiteInfo();

   const errorMessages = {
      400: {
         title: _js('Bad Request'),
         description: _js(
            'The request could not be processed due to invalid input. Please check and try again.'
         ),
      },
      403: {
         title: _js('Forbidden Access'),
         description: _js("You're not authorized to view this page."),
      },
      404: {
         title: _js('Page Not Found'),
         description: _js("We can't find the page you're looking for."),
      },
      500: {
         title: _js('Server Problem'),
         description: _js(
            "We are clearly experiencing technical difficulties. We'll get right on that."
         ),
      },
      503: {
         title: _js('Service Unavailable'),
         description: _js('The service is temporarily unavailable.'),
      },
   };

   const errorCode = isErrorCode(error.code) ? error.code : 500;

   const { title, description } =
      hasContent(error.title) && hasContent(error.description) ? error : errorMessages[errorCode];
   const siteName = siteInfo?.title;
   const showLinks = siteInfo?.['feature-guide'] || siteInfo?.['feature-answers'];

   return (
      <Flex direction="column" align="center" textAlign="center" mb={10}>
         <Heading fontSize={50} fontWeight={600} mt={6} mb={4}>
            {errorCode}
         </Heading>
         <Text fontSize={20} fontWeight={600} textTransform="uppercase">
            {title} - {siteName}
         </Text>
         <Box background="red" w={50} h="2px" my={7} />
         <Text fontSize={18} w={320}>
            {description}
         </Text>
         {showLinks && (
            <Box mt={6} mb={8}>
               {siteInfo['feature-guide'] && (
                  <Text>
                     {_js("Find the content you're looking for by browsing our ")}
                     <NextLink href="/Guide" legacyBehavior>
                        <Link color="blue.500">{_js('guides.')}</Link>
                     </NextLink>
                  </Text>
               )}
               {siteInfo['feature-answers'] && (
                  <Text>
                     {_js("You can also browse all of our community's ")}
                     <NextLink href="/Answers/Categories" legacyBehavior>
                        <Link color="blue.500">{_js('questions by Category.')}</Link>
                     </NextLink>
                  </Text>
               )}
            </Box>
         )}
      </Flex>
   );
};
